import BrandingResource from 'flow/api/brandingv2';
import HomeClient from 'flow/api-clients/home-client';
import isElectron from 'is-electron';

import {
    FALLBACK_THEME_DETAILS,
    setBrandFromAPI,
    setEnvironmentBrand,
    setEnvironmentLogos,
    setLogosFromAPI,
} from 'flow/brands/brands';
import {
    getApiHost,
    getDomainPath,
    FALLBACK_BRANDING_URL,
} from 'flow/utils/brandingv2-utils';
import { isHTMLPage } from 'flow/utils/app-utils';
import {
    IS_UAE_HOST,
    IS_UAE_INTERNAL_HOST,
    UAE_APP_URL,
} from 'flow/utils/browser-utils-TW';
import {
    fetchBrandingFromGeonex,
    fetchBrandingLogosFromGeonex,
} from 'flow/utils/geonex-api-utils';
import { isGeonexConfigured, apiUrl } from 'app-config';
import Actions from 'flow/actions';

const DEFAULT_BRANDING_URL = 'https://flow.telavox.com';

// Opt a brand-colored element into the dark-brand palette regardless of the
// user's own light/dark setting — for surfaces that are always dark, like the
// in-meeting video chat, as opposed to the regular Messages tab reusing the
// same components elsewhere.
export const VIDEO_DARK_BRAND_CLASS = 'video-dark-brand';

// Internal UAE hosts reduce to telavox.com, so they borrow Etisalat's domain for branding.
const getElectronBrandingHost = (isRegionCheck) =>
    (IS_UAE_INTERNAL_HOST && UAE_APP_URL) ||
    apiUrl ||
    (isRegionCheck ? FALLBACK_BRANDING_URL : DEFAULT_BRANDING_URL);

export const getBrandingDomainPath = (isRegionCheck = false) => {
    const hostname = isElectron()
        ? getElectronBrandingHost(isRegionCheck)
        : window.location.hostname;
    return getDomainPath(hostname, isRegionCheck);
};

export const fetchBranding = () => {
    return new Promise((resolve, reject) => {
        BrandingResource.getBranding({
            onResult: (result) => {
                setBrandingData(result);
                return resolve();
            },
            onError: (error) => {
                return reject(new Error(error));
            },
        });
    }).catch((e) => console.error('Error when fetching brand from API: ', e));
};

export const checkRoamingStatus = (dispatch) => {
    if (IS_UAE_HOST && !IS_UAE_INTERNAL_HOST && isElectron()) {
        const hostname = apiUrl || DEFAULT_BRANDING_URL;
        const request = new window.XMLHttpRequest();
        request.open('GET', `${hostname}/roaming-status`, true);

        request.onreadystatechange = () => {
            if (request.readyState === 4) {
                dispatch(setIsRoaming(request.status === 404));
            }
        };

        request.send();
    }
};

export const fetchDomainBranding = (canUseRegionCheck = false) => {
    const isRegionCheckEnabledForGuest =
        canUseRegionCheck && isGeonexConfigured;
    const domainPath = getBrandingDomainPath(isRegionCheckEnabledForGuest);

    if (!domainPath) {
        setApiHost();
        return Promise.resolve();
    }

    if (isRegionCheckEnabledForGuest) {
        return fetchBrandingFromGeonex(domainPath)
            .then((result) => {
                if (result) {
                    setBrandingData(result, true);
                    return Promise.resolve();
                } else {
                    console.warn(
                        'Failed when fetching branding from Geonex, Falling back to default ',
                    );
                    return fallbackToDomainBranding(domainPath);
                }
            })
            .catch((e) => {
                console.error(
                    'Error when fetching branding from Geonex, Falling back to default: ',
                    e,
                );
                return fallbackToDomainBranding(domainPath);
            })
            .finally(() => {
                setApiHost();
            });
    }

    return fallbackToDomainBranding(domainPath).finally(() => {
        setApiHost();
    });
};

export const fetchDomainBrandingLogoUrls = (canUseRegionCheck = false) => {
    const isRegionCheckEnabledForGuest =
        canUseRegionCheck && isGeonexConfigured;
    const domainPath = getBrandingDomainPath(isRegionCheckEnabledForGuest);

    if (!domainPath) {
        console.warn(
            'fetchDomainBrandingLogoUrls: domainPath is falsy; skipping domain branding logo lookup.',
        );
        return Promise.resolve();
    }

    if (isRegionCheckEnabledForGuest) {
        return fetchBrandingLogosFromGeonex(domainPath)
            .then((result) => {
                if (result) {
                    setBrandingLogos(result, true);
                    return Promise.resolve();
                } else {
                    console.warn(
                        'Failed when fetching branding logos from Geonex, Falling back to default ',
                    );
                    return fallbackToDomainBrandingLogoUrls(domainPath);
                }
            })
            .catch((e) => {
                console.error(
                    'Error when fetching branding logos from Geonex, Falling back to default: ',
                    e,
                );
                return fallbackToDomainBrandingLogoUrls(domainPath);
            });
    }

    return fallbackToDomainBrandingLogoUrls(domainPath);
};

const setIsRoaming = (isRoaming) => {
    return {
        type: Actions.IS_ROAMING,
        isRoaming,
    };
};

// When fetching branding for a domain we assume it's always
// either from the url or the electron application branding.
export const setBrandingData = (brandingV2DTO, isDomainFetch = false) => {
    if (!brandingV2DTO) {
        return;
    }
    setBrandFromAPI(brandingV2DTO);
    updateBrandingTheme(brandingV2DTO.theme);

    if (isDomainFetch) {
        setEnvironmentBrand(brandingV2DTO);
    }
};

export const setBrandingLogos = (logosDTO, isDomainFetch = false) => {
    if (!logosDTO) {
        return;
    }
    setLogosFromAPI(logosDTO);

    if (isDomainFetch) {
        setEnvironmentLogos(logosDTO);
    }
};

export const updateBrandingTheme = (theme) => {
    if (!theme) {
        return;
    }
    updateCssVariables(theme);
};

export const setDefaultBrandingData = () => {
    updateCssVariables(FALLBACK_THEME_DETAILS);
};

function updateCssVariables(theme) {
    const ligthColorsToMap = {
        '--color-main': theme.brand.light,
        '--color-green': theme.green.light,
        '--color-red': theme.red.light,
        '--color-yellow': theme.yellow.light,
        '--color-link': theme.link.light,
        '--color-chat-you': theme.chatYou.light,
        '--color-conference': theme.pbxConference.light,
        '--color-refer': theme.pbxRefer.light,
        '--color-huntgroup': theme.pbxHuntgroup.light,
        '--color-channel': theme.pbxChannel.light,
        '--color-voicemail': theme.pbxVoicemail.light,
        '--color-connection': theme.pbxConnection.light,
        '--color-flavor-1': theme.flavor1.light,
        '--color-flavor-2': theme.flavor2.light,
        '--color-flavor-3': theme.flavor3.light,
        '--color-flavor-4': theme.flavor4.light,
        '--color-flavor-5': theme.flavor5.light,
        '--color-flavor-6': theme.flavor6.light,
        '--color-flavor-7': theme.flavor7.light,
        '--color-flavor-8': theme.flavor8.light,
        '--color-flavor-9': theme.flavor9.light,
        '--color-iconOnBrand': theme.iconOnBrand.light,
        '--color-textOnBrand': theme.textOnBrand.light,
    };
    const darkColorsToMap = {
        '--color-main': theme.brand.dark,
        '--color-green': theme.green.dark,
        '--color-red': theme.red.dark,
        '--color-yellow': theme.yellow.dark,
        '--color-link': theme.link.dark,
        '--color-chat-you': theme.chatYou.dark,
        '--color-conference': theme.pbxConference.dark,
        '--color-refer': theme.pbxRefer.dark,
        '--color-huntgroup': theme.pbxHuntgroup.dark,
        '--color-channel': theme.pbxChannel.dark,
        '--color-voicemail': theme.pbxVoicemail.dark,
        '--color-connection': theme.pbxConnection.dark,
        '--color-flavor-1': theme.flavor1.dark,
        '--color-flavor-2': theme.flavor2.dark,
        '--color-flavor-3': theme.flavor3.dark,
        '--color-flavor-4': theme.flavor4.dark,
        '--color-flavor-5': theme.flavor5.dark,
        '--color-flavor-6': theme.flavor6.dark,
        '--color-flavor-7': theme.flavor7.dark,
        '--color-flavor-8': theme.flavor8.dark,
        '--color-flavor-9': theme.flavor9.dark,
        '--color-iconOnBrand': theme.iconOnBrand.dark,
        '--color-textOnBrand': theme.textOnBrand.dark,
    };

    // Apply light mode variables in a dynamically created style element
    let lightStyleElement = document.getElementById('light-mode-vars');
    if (!lightStyleElement) {
        lightStyleElement = document.createElement('style');
        lightStyleElement.id = 'light-mode-vars';
        document.head.appendChild(lightStyleElement);
    }

    const lightVariablesCSS = Object.entries(ligthColorsToMap)
        .map(([key, value]) => `${key}: ${value};`)
        .join(' ');

    lightStyleElement.innerHTML = `html { ${lightVariablesCSS} }`;

    // Apply dark mode variables in a dynamically created style element
    let darkStyleElement = document.getElementById('dark-mode-vars');
    if (!darkStyleElement) {
        darkStyleElement = document.createElement('style');
        darkStyleElement.id = 'dark-mode-vars';
        document.head.appendChild(darkStyleElement);
    }

    const darkVariablesCSS = Object.entries(darkColorsToMap)
        .map(([key, value]) => `${key}: ${value};`)
        .join(' ');

    darkStyleElement.innerHTML = `html.dark { ${darkVariablesCSS} }`;

    // Same dark palette, but under a plain class rather than html.dark — for
    // surfaces that are always dark (the in-meeting video chat) regardless of
    // the user's own light/dark setting. See VIDEO_DARK_BRAND_CLASS.
    let videoDarkStyleElement = document.getElementById(
        'video-dark-brand-vars',
    );
    if (!videoDarkStyleElement) {
        videoDarkStyleElement = document.createElement('style');
        videoDarkStyleElement.id = 'video-dark-brand-vars';
        document.head.appendChild(videoDarkStyleElement);
    }
    videoDarkStyleElement.innerHTML = `.${VIDEO_DARK_BRAND_CLASS} { ${darkVariablesCSS} }`;
}

const fallbackToDomainBranding = (domainPath) => {
    return new Promise((resolve) => {
        BrandingResource.getDomainBranding(
            {
                onResult: (result) => {
                    if (!isHTMLPage(result)) {
                        setBrandingData(result, true);
                    }
                    resolve();
                },
                onError: (error) => {
                    console.error(
                        'Error when fetching domain brand from API: ',
                        error,
                    );
                    resolve(error);
                },
            },
            domainPath,
        );
    });
};

const fallbackToDomainBrandingLogoUrls = (domainPath) => {
    return new Promise((resolve, reject) => {
        BrandingResource.getBrandingLogoUrlsForDomain(
            {
                onResult: (result) => {
                    setBrandingLogos(result, true);
                    return resolve();
                },
                onError: (error) => {
                    return reject(new Error(error));
                },
            },
            domainPath,
        );
    }).catch((e) =>
        console.error('Error when fetching branding logo URLs from API: ', e),
    );
};

/**
 * Once we have logged in we don't want to
 * change the api host because then all calls
 * to another host would be unauthorized.
 */
const setApiHost = () => {
    const apiHost = getApiHost();
    const apiEndpoint = apiHost + HomeClient.apiPath;
    HomeClient.apiHost = apiHost;
    HomeClient.endpoint = apiEndpoint;
};

export const fetchBrandingLogoUrls = () => {
    return new Promise((resolve, reject) => {
        BrandingResource.getBrandingLogoUrls({
            onResult: (result) => {
                setLogosFromAPI(result);
                return resolve();
            },
            onError: (error) => {
                return reject(new Error(error));
            },
        });
    }).catch((e) =>
        console.error('Error when fetching branding logo URLs from API: ', e),
    );
};
